(() => {
  const instances = new WeakMap();

  const destroy = root => {
    const instance = instances.get(root);
    if (!instance) return;
    instance.observer?.disconnect();
    instance.controller.abort();
    instances.delete(root);
  };

  const initialize = root => {
    destroy(root);

    const controller = new AbortController();
    const productSlides = [...root.querySelectorAll('[data-fp-slide="products"]')];
    const quoteSlides = [...root.querySelectorAll('[data-fp-slide="quotes"]')];
    const mediaSlides = [...root.querySelectorAll('[data-fp-slide="media"]')];
    const tracks = [...root.querySelectorAll('[data-fp-track]')];
    const testimonialPanel = root.querySelector('[data-fp-testimonials]');
    const previous = root.querySelector('[data-fp-prev]');
    const next = root.querySelector('[data-fp-next]');
    const fraction = root.querySelector('[data-fp-fraction]');
    const slideStatus = root.querySelector('[data-fp-status]');
    const cartStatus = root.querySelector('[data-fp-cart-status]');
    const total = productSlides.length;
    let activeIndex = 0;
    let opener = null;

    const updateSlides = (slides, index) => {
      slides.forEach((slide, slideIndex) => {
        const active = slideIndex === index;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', String(!active));
        if (active) slide.removeAttribute('inert');
        else slide.setAttribute('inert', '');
      });
    };

    const update = (requestedIndex, announce = true) => {
      const index = Math.max(0, Math.min(requestedIndex, total - 1));
      activeIndex = index;
      tracks.forEach(track => track.style.setProperty('--fp-index', index));
      updateSlides(productSlides, index);
      updateSlides(quoteSlides, index);
      updateSlides(mediaSlides, index);

      const quote = quoteSlides[index];
      if (quote && testimonialPanel) {
        testimonialPanel.style.setProperty('--fp-panel-background', quote.dataset.panelBackground);
        testimonialPanel.style.setProperty('--fp-panel-text', quote.dataset.panelText);
      }

      if (previous) {
        previous.disabled = index === 0;
        previous.setAttribute('aria-disabled', String(index === 0));
      }
      if (next) {
        next.disabled = index === total - 1;
        next.setAttribute('aria-disabled', String(index === total - 1));
      }
      if (fraction) fraction.textContent = `${index + 1} / ${total}`;
      if (announce && slideStatus) slideStatus.textContent = `Slide ${index + 1} of ${total}`;
    };

    previous?.addEventListener('click', () => update(activeIndex - 1), { signal: controller.signal });
    next?.addEventListener('click', () => update(activeIndex + 1), { signal: controller.signal });

    if (root.dataset.enableSwipe === 'true' && total > 1) {
      root.querySelectorAll('[data-fp-viewport="products"], [data-fp-viewport="quotes"]').forEach(viewport => {
        let startX = 0;
        viewport.addEventListener('touchstart', event => {
          startX = event.changedTouches[0].clientX;
        }, { passive: true, signal: controller.signal });
        viewport.addEventListener('touchend', event => {
          const distance = event.changedTouches[0].clientX - startX;
          if (Math.abs(distance) < 30) return;
          update(activeIndex + (distance < 0 ? 1 : -1));
        }, { passive: true, signal: controller.signal });
      });
    }

    root.addEventListener('click', event => {
      const openButton = event.target.closest('[data-fp-dialog-open]');
      if (openButton) {
        const dialog = root.querySelector(`#${CSS.escape(openButton.dataset.fpDialogOpen)}`);
        if (dialog) {
          opener = openButton;
          dialog.showModal();
          dialog.querySelector('select, button')?.focus();
        }
        return;
      }

      const closeButton = event.target.closest('[data-fp-dialog-close]');
      if (closeButton) closeButton.closest('dialog')?.close();
    }, { signal: controller.signal });

    root.querySelectorAll('dialog').forEach(dialog => {
      dialog.addEventListener('click', event => {
        if (event.target === dialog) dialog.close();
      }, { signal: controller.signal });
      dialog.addEventListener('close', () => opener?.focus(), { signal: controller.signal });
    });

    root.addEventListener('submit', async event => {
      const form = event.target.closest('[data-fp-cart-form]');
      if (!form) return;
      event.preventDefault();

      const button = form.querySelector('button[type="submit"]');
      const variantId = new FormData(form).get('id');
      if (!variantId || button?.disabled) return;

      button?.setAttribute('aria-busy', 'true');
      try {
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] })
        });
        if (!response.ok) throw new Error('Cart request failed');

        const cartResponse = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, { headers: { Accept: 'application/json' } });
        const cart = await cartResponse.json();
        document.querySelectorAll('.bag-count').forEach(count => { count.textContent = cart.item_count; });
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        if (cartStatus) cartStatus.textContent = `${form.dataset.productTitle} added to your bag.`;
        form.closest('dialog')?.close();
      } catch {
        if (cartStatus) cartStatus.textContent = 'This item could not be added. Please try again.';
      } finally {
        button?.removeAttribute('aria-busy');
      }
    }, { signal: controller.signal });

    document.addEventListener('shopify:block:select', event => {
      const slide = productSlides.find(item => item.dataset.blockId === event.detail.blockId);
      if (slide) update(productSlides.indexOf(slide), false);
    }, { signal: controller.signal });

    root.classList.add('is-visible');

    update(0, false);
    instances.set(root, { controller, observer: null });
  };

  const initializeWithin = (scope = document) => {
    if (scope.matches?.('[data-favorite-products]')) initialize(scope);
    scope.querySelectorAll?.('[data-favorite-products]').forEach(initialize);
  };

  initializeWithin();
  document.addEventListener('shopify:section:load', event => initializeWithin(event.target));
  document.addEventListener('shopify:section:unload', event => {
    const root = event.target.matches?.('[data-favorite-products]')
      ? event.target
      : event.target.querySelector?.('[data-favorite-products]');
    if (root) destroy(root);
  });
})();
